import { useMemo } from 'react';

import { useQuery } from '@apollo/client/react';

import { useDebouncedValue } from '@clutter/hooks';

import { AvalaraTaxItemCode, SalesTaxDocument } from '@graphql/platform';

import { useSharedCheckoutContext } from '@root/components/checkout/context';
import { TaxRateQueryState } from '@root/components/checkout/hooks/use_storage_tax_rate';

import { hasChallengerPickupAddress } from '../completion';

const TAX_ZIP_DEBOUNCE_MS = 400;
const TAX_FETCH_POLICY = 'cache-first' as const;

export type ChallengerTaxRates = {
  storage: TaxRateQueryState;
  protection: TaxRateQueryState;
};

/** ZIP used for challenger SalesTax queries (matches hook + summary display). */
export const challengerTaxZip = (
  zip: string | undefined,
  address: Parameters<typeof hasChallengerPickupAddress>[0],
): string | undefined => {
  if (hasChallengerPickupAddress(address)) {
    return address!.zip!.trim();
  }
  return zip?.trim() || undefined;
};

/**
 * Single tax fetch for challenger page 1 — debounced ZIP, cache-first, staggered
 * storage vs protection queries to reduce GraphQL rate-limit bursts.
 */
export const useChallengerTaxRates = (): ChallengerTaxRates => {
  const {
    flowState: { values },
  } = useSharedCheckoutContext();

  const rawZip = useMemo(
    () => challengerTaxZip(values.zip, values.address),
    [values.address, values.zip],
  );

  const zip = useDebouncedValue(rawZip, TAX_ZIP_DEBOUNCE_MS);

  const storageResult = useQuery(SalesTaxDocument, {
    variables: {
      zipCode: zip!,
      avalaraItemCode: AvalaraTaxItemCode.Storage,
    },
    skip: !zip,
    fetchPolicy: TAX_FETCH_POLICY,
    notifyOnNetworkStatusChange: false,
  });

  const protectionResult = useQuery(SalesTaxDocument, {
    variables: {
      zipCode: zip!,
      avalaraItemCode: AvalaraTaxItemCode.Default,
    },
    skip: !zip || storageResult.loading,
    fetchPolicy: TAX_FETCH_POLICY,
    notifyOnNetworkStatusChange: false,
  });

  return {
    storage: {
      percent:
        storageResult.data?.salesTax != null
          ? storageResult.data.salesTax.percent
          : undefined,
      loading: Boolean(zip) && storageResult.loading,
      error: storageResult.error ?? undefined,
    },
    protection: {
      percent:
        protectionResult.data?.salesTax != null
          ? protectionResult.data.salesTax.percent
          : undefined,
      loading: Boolean(zip) && protectionResult.loading,
      error: protectionResult.error ?? undefined,
    },
  };
};
